import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  Package,
  FileText,
  Truck,
  Clock,
  AlertCircle,
  Filter,
  X,
  RefreshCw,
  Info,
  ChevronLeft,
  ChevronRight as ChevronRightIcon
} from 'lucide-react';
import {
  POrderHistoryDto,
  POrderHistoryLineDto,
  POrderHistoryTransactionDto,
  POrderHistoryQueryParams,
  PagedResult,
  calculateOrderCompletionPercentage,
  calculateLineCompletionPercentage
} from '../../../../types/purchaseOrderHistory';
import { purchaseOrderHistoryService } from '../../../../services/purchaseOrderHistoryService';
import { Card } from '../../../common/Card';
import { FilterPanel } from './FilterPanel';
import { OrderRow } from './OrderRow';
import { OrderDetailsModal } from './OrderDetailsModal';
import { exportToExcel } from './excelExport';

interface PurchaseOrderHistoryListProps {
  onBack?: () => void;
}

interface ExpandedState {
  orders: Set<number>;
  lines: Set<string>; // Format: "orderId-lineNo"
}

export const PurchaseOrderHistoryList: React.FC<PurchaseOrderHistoryListProps> = ({ onBack }) => {
  // Data state
  const [orders, setOrders] = useState<PagedResult<POrderHistoryDto> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(true);
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default 30 days ago
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const [supplierNo, setSupplierNo] = useState('');
  const [orderNoERP, setOrderNoERP] = useState('');
  const [dateError, setDateError] = useState<string | null>(null);

  // Pagination state
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // UI state
  const [expandedState, setExpandedState] = useState<ExpandedState>({
    orders: new Set(),
    lines: new Set()
  });
  const [selectedOrder, setSelectedOrder] = useState<POrderHistoryDto | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Validate date range
  useEffect(() => {
    if (!fromDate || !toDate) {
      setDateError('Ugyldig dato');
      return;
    }

    const daysDiff = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 90) {
      setDateError('Maksimalt 90 dagers periode tillatt');
    } else if (daysDiff < 0) {
      setDateError('Fra-dato må være før til-dato');
    } else {
      setDateError(null);
    }
  }, [fromDate, toDate]);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (dateError) return;

    setLoading(true);
    setError(null);

    try {
      const params: POrderHistoryQueryParams = {
        fromDate,
        toDate,
        pageNumber,
        pageSize
      };

      if (supplierNo.trim()) {
        params.supplierNo = supplierNo.trim();
      }

      if (orderNoERP.trim()) {
        params.orderNoERP = orderNoERP.trim();
      }

      const data = await purchaseOrderHistoryService.getOrderHistory(params);
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En feil oppstod ved henting av data');
      console.error('Error fetching purchase order history:', err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, pageNumber, pageSize, supplierNo, orderNoERP, dateError]);

  // Initial load and refresh on filter changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPageNumber(1);
  }, [fromDate, toDate, supplierNo, orderNoERP, pageSize]);

  // Toggle order expansion
  const toggleOrder = (orderId: number) => {
    setExpandedState(prev => {
      const newOrders = new Set(prev.orders);
      if (newOrders.has(orderId)) {
        newOrders.delete(orderId);
      } else {
        newOrders.add(orderId);
      }
      return { ...prev, orders: newOrders };
    });
  };

  // Toggle line expansion
  const toggleLine = (orderId: number, lineNo: number) => {
    const key = `${orderId}-${lineNo}`;
    setExpandedState(prev => {
      const newLines = new Set(prev.lines);
      if (newLines.has(key)) {
        newLines.delete(key);
      } else {
        newLines.add(key);
      }
      return { ...prev, lines: newLines };
    });
  };

  // Expand all orders
  const expandAll = () => {
    if (!orders?.items) return;
    const allOrderIds = orders.items.map(o => o.id);
    setExpandedState(prev => ({
      ...prev,
      orders: new Set(allOrderIds)
    }));
  };

  // Collapse all
  const collapseAll = () => {
    setExpandedState({
      orders: new Set(),
      lines: new Set()
    });
  };

  // Show order details modal
  const showOrderDetails = (order: POrderHistoryDto) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  // Export to Excel
  const handleExport = async () => {
    if (!orders?.items || orders.items.length === 0) {
      setError('Ingen data å eksportere');
      return;
    }

    setExporting(true);
    try {
      const filename = `bestilling_historikk_${purchaseOrderHistoryService.formatDateForApi(fromDate)}_${purchaseOrderHistoryService.formatDateForApi(toDate)}.xlsx`;
      await exportToExcel(orders.items, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eksport feilet');
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    setFromDate(date);
    setToDate(new Date());
    setSupplierNo('');
    setOrderNoERP('');
    setPageNumber(1);
  };

  // Calculate summary statistics
  const summaryStats = orders?.items ? purchaseOrderHistoryService.getSummaryStatistics(orders.items) : null;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            {onBack && (
              <button
                onClick={onBack}
                className="text-gray-600 hover:text-gray-900 transition-colors"
                aria-label="Gå tilbake"
              >
                <ChevronLeft size={24} />
              </button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bestilling Historikk</h1>
              {summaryStats && (
                <p className="text-sm text-gray-600 mt-1">
                  {summaryStats.totalOrders} ordre · {summaryStats.totalLines} linjer · {summaryStats.totalTransactions} transaksjoner
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                showFilters
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={showFilters ? 'Skjul filtre' : 'Vis filtre'}
            >
              <Filter className="inline-block mr-2" size={18} />
              Filtre
            </button>

            <button
              onClick={fetchData}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              title="Oppdater"
            >
              <RefreshCw className={`inline-block mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
              Oppdater
            </button>

            <button
              onClick={handleExport}
              disabled={exporting || !orders?.items || orders.items.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Eksporter til Excel"
            >
              <Download className="inline-block mr-2" size={18} />
              {exporting ? 'Eksporterer...' : 'Eksporter'}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <FilterPanel
            fromDate={fromDate}
            toDate={toDate}
            supplierNo={supplierNo}
            orderNoERP={orderNoERP}
            dateError={dateError}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            onSupplierNoChange={setSupplierNo}
            onOrderNoERPChange={setOrderNoERP}
            onClearFilters={clearFilters}
          />
        )}

        {/* Error display */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="text-red-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Feil</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800 ml-3"
            >
              <X size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading && orders === null ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="animate-spin mx-auto mb-4 text-blue-600" size={48} />
              <p className="text-gray-600">Laster inn bestillingshistorikk...</p>
            </div>
          </div>
        ) : orders && orders.items.length > 0 ? (
          <>
            {/* Expand/Collapse controls */}
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={expandAll}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  Utvid alle
                </button>
                <button
                  onClick={collapseAll}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                >
                  Kollaps alle
                </button>
              </div>

              <p className="text-sm text-gray-600">
                Viser {((pageNumber - 1) * pageSize) + 1}-{Math.min(pageNumber * pageSize, orders.totalRecords)} av {orders.totalRecords} ordre
              </p>
            </div>

            {/* Orders list */}
            <div className="space-y-4">
              {orders.items.map(order => (
                <OrderRow
                  key={order.id}
                  order={order}
                  isExpanded={expandedState.orders.has(order.id)}
                  expandedLines={expandedState.lines}
                  onToggle={() => toggleOrder(order.id)}
                  onToggleLine={(lineNo) => toggleLine(order.id, lineNo)}
                  onShowDetails={() => showOrderDetails(order)}
                />
              ))}
            </div>

            {/* Pagination */}
            {orders.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Linjer per side:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                    disabled={!orders.hasPreviousPage}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  <span className="text-sm text-gray-700">
                    Side {pageNumber} av {orders.totalPages}
                  </span>

                  <button
                    onClick={() => setPageNumber(Math.min(orders.totalPages, pageNumber + 1))}
                    disabled={!orders.hasNextPage}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRightIcon size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Package className="mx-auto mb-4 text-gray-400" size={64} />
              <p className="text-gray-600 text-lg font-medium">Ingen bestillinger funnet</p>
              <p className="text-gray-500 text-sm mt-2">
                Prøv å justere datofiltrene eller søkekriteriene
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
};
